import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Button from './components/ui/Button';
import ErrorBoundary from './components/ui/ErrorBoundary';
import VinSearch from './components/VinSearch';
import PackageCard from './components/PackageCard';
import MaterialCard from './components/MaterialCard';
import PriceSummary from './components/PriceSummary';
import SideNav from './components/SideNav';
import WelcomeScreen from './components/WelcomeScreen';
import CelebrationOverlay from './components/ui/CelebrationOverlay';
import ToastStack from './components/ui/ToastStack';
import { ToastProvider } from './context/ToastContext';
import Ticker from './components/ui/Ticker';
import Tooltip from './components/ui/Tooltip';
import EstimateRightPanel from './components/EstimateRightPanel';
import IOSAddToHomeScreen from './components/IOSAddToHomeScreen';
import Dashboard from './components/dashboard/Dashboard';
import Settings from './components/Settings';
import SettingsShell from './components/settings/SettingsShell';
import CustomerForm from './components/CustomerForm';
import AuditLog from './components/AuditLog';
import SiteBugsPage from './components/beta/SiteBugsPage';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { GamificationProvider, useGamification } from './context/GamificationContext';
import { RolesProvider, useRoles, ROLES } from './context/RolesContext';
import { UnitsProvider } from './context/UnitsContext';
import { FeedbackProvider } from './context/FeedbackContext';
import { FeatureFlagsProvider, useFeatureFlags } from './context/FeatureFlagsContext';
import { AuditLogProvider, useAuditLog } from './context/AuditLogContext';
import PerformancePage from './components/performance/PerformancePage';
import WorkflowPage from './components/workflow/WorkflowPage';
import LeadHubPage from './components/leadhub/LeadHubPage';
import CustomersPage from './components/lists/CustomersPage';
import VehiclesPage from './components/lists/VehiclesPage';
import ManufacturersPage from './components/lists/ManufacturersPage';
import EstimatesPage from './components/estimates/EstimatesPage';
import InvoicesPage from './components/invoices/InvoicesPage';
import NotificationsPage from './components/notifications/NotificationsPage';
import ReportsPage from './components/reports/ReportsPage';
import ClientPortalPage from './components/portal/ClientPortalPage';
import { NotificationsProvider, useNotifications } from './context/NotificationsContext';
import { EstimateProvider, useEstimates } from './context/EstimateContext';
import { recordEstimateOutcome } from './lib/learningAgent';
import { InvoiceProvider, useInvoices } from './context/InvoiceContext';
import { CustomerProvider } from './context/CustomerContext';
import { InventoryProvider } from './context/InventoryContext';
import InventoryPage from './components/inventory/InventoryPage';
// NOTE: SideNav 'inventory' nav key will be wired when nav is restructured.
import WrapMindChat from './components/chat/WrapMindChat';
import HelpPage from './components/HelpPage';
import SetupWizard from './components/setup/SetupWizard';
import IntelligencePage from './components/intelligence/IntelligencePage';
import ShopProfilesPage from './components/shops/ShopProfilesPage';
import MarketingPage from './components/marketing/MarketingPage';
import SchedulingPage from './components/scheduling/SchedulingPage';
import WorkOrdersPage from './components/orders/WorkOrdersPage';
import { WorkOrderProvider } from './context/WorkOrderContext';
import SubcontractorsPage from './components/subcontractors/SubcontractorsPage';
import { SubcontractorProvider } from './context/SubcontractorContext';
import CommandPalette from './components/ui/CommandPalette';
import { MarketingProvider } from './context/MarketingContext';
import { LeadsProvider } from './context/LeadsContext';
import { SchedulingProvider } from './context/SchedulingContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { TickerProvider } from './context/TickerContext';
import { LocationProvider } from './context/LocationContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WmStaffProvider, useWmStaff } from './context/WmStaffContext';
import StaffImpersonationBanner from './components/staff/StaffImpersonationBanner';
import StaffPortal from './components/staff/StaffPortal';
import MyAccounts from './components/staff/MyAccounts';
import StaffSettingsTab from './components/staff/StaffSettingsTab';
import AuthPage from './components/auth/AuthPage';
import LocationSwitcher from './components/ui/LocationSwitcher';
import CustomerEstimateView from './components/portal/CustomerEstimateView';
import { logEvent, EVENT_TYPES, resetSession } from './lib/analytics';
import BottomTabBar from './components/ui/BottomTabBar';

// ─── Placeholder view ────────────────────────────────────────────────────────

// VIEW_META provides fallback labels/subtitles for any view without a dedicated component.
// Views that have real page components are intentionally omitted.
const VIEW_META = {
  performance:     { label: 'Performance', subtitle: 'Team XP leaderboard and rewards.' },
  'audit-log':     { label: 'Audit Log',   subtitle: 'Full activity log for all users.' },
  'site-bugs':     { label: 'Site Bugs',   subtitle: 'Track and manage reported bugs and feedback.' },
};

// ── Parcel Tracking ───────────────────────────────────────────────────────────
const PT_CARRIER_URLS = {
  UPS:    'https://www.ups.com/track?tracknum=',
  FedEx:  'https://www.fedex.com/fedextrack/?trknbr=',
  USPS:   'https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=',
  DHL:    'https://www.dhl.com/us-en/home/tracking.html?tracking-id=',
  Amazon: 'https://track.amazon.com/tracking/',
};
const PT_CARRIER_STYLE = {
  UPS:    { bg: 'bg-amber-700',  text: 'text-white',      label: 'UPS'    },
  FedEx:  { bg: 'bg-purple-700', text: 'text-white',      label: 'FedEx'  },
  USPS:   { bg: 'bg-blue-700',   text: 'text-white',      label: 'USPS'   },
  DHL:    { bg: 'bg-yellow-400', text: 'text-red-700',    label: 'DHL'    },
  Amazon: { bg: 'bg-[#FF9900]',  text: 'text-[#232F3E]', label: 'Amazon' },
};
const PT_STATUS = {
  ordered:          { label: 'Ordered',          color: 'text-gray-500 dark:text-gray-400',       bg: 'bg-gray-100 dark:bg-gray-700/40',      dot: 'bg-gray-400'    },
  in_transit:       { label: 'In Transit',        color: 'text-blue-600 dark:text-blue-400',       bg: 'bg-blue-50 dark:bg-blue-900/30',       dot: 'bg-blue-500'    },
  out_for_delivery: { label: 'Out for Delivery',  color: 'text-amber-600 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/30',     dot: 'bg-amber-500'   },
  delivered:        { label: 'Delivered',         color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/30', dot: 'bg-emerald-500' },
  exception:        { label: 'Exception',         color: 'text-red-600 dark:text-red-400',         bg: 'bg-red-50 dark:bg-red-900/30',         dot: 'bg-red-500'     },
};
const PT_INIT = [
  { id:'SHP-001', carrier:'UPS',   tracking:'1Z999AA10123456784',  status:'delivered',        item:'Avery Dennison SW900 — Matte Black (3 rolls)', ordered:'2026-04-08', shipped:'2026-04-09', eta:'2026-04-11', delivered:'2026-04-11', supplier:'Wrap Supply Co.',    service:'UPS Ground',    weight:'18.2 lbs', origin:'Atlanta, GA',      destination:'Miami, FL',
    events:[{date:'2026-04-11',time:'2:14 PM',location:'Miami, FL',           desc:'Delivered — Front door'},{date:'2026-04-10',time:'7:18 AM', location:'Miami, FL',          desc:'Out for delivery'},{date:'2026-04-09',time:'11:45 PM',location:'Fort Lauderdale, FL', desc:'Arrived at destination facility'},{date:'2026-04-09',time:'6:00 AM', location:'Atlanta, GA',        desc:'Package picked up by carrier'}] },
  { id:'SHP-002', carrier:'FedEx', tracking:'774899172137',         status:'in_transit',       item:'KPMF K75400 — Gloss Galaxy Blue (2 rolls)',    ordered:'2026-04-12', shipped:'2026-04-12', eta:'2026-04-16', delivered:null,          supplier:'WrapFolio Direct', service:'FedEx Ground',  weight:'9.4 lbs',  origin:'Los Angeles, CA',  destination:'Miami, FL',
    events:[{date:'2026-04-14',time:'3:42 PM',location:'Dallas, TX',           desc:'In transit'},{date:'2026-04-13',time:'8:15 AM',location:'Phoenix, AZ',            desc:'Arrived at sort facility'},{date:'2026-04-12',time:'2:30 PM',location:'Los Angeles, CA',        desc:'Picked up'}] },
  { id:'SHP-003', carrier:'USPS',  tracking:'9400111899223937920',  status:'out_for_delivery', item:'VViViD Conform Chrome — Gold (1 roll)',         ordered:'2026-04-13', shipped:'2026-04-13', eta:'2026-04-15', delivered:null,          supplier:'WrapFolio Direct', service:'USPS Priority', weight:'3.1 lbs',  origin:'Chicago, IL',      destination:'Miami, FL',
    events:[{date:'2026-04-15',time:'8:02 AM',location:'Miami, FL',            desc:'Out for delivery'},{date:'2026-04-14',time:'11:30 PM',location:'Miami, FL',           desc:'Arrived at post office'},{date:'2026-04-13',time:'5:45 PM',location:'Chicago, IL',            desc:'Accepted at USPS facility'}] },
  { id:'SHP-004', carrier:'FedEx', tracking:'774502912984',         status:'ordered',          item:'3M 1080 — Satin White (5 rolls)',               ordered:'2026-04-14', shipped:null,         eta:'2026-04-18', delivered:null,          supplier:'Wrap Supply Co.',    service:'FedEx Ground',  weight:'31.0 lbs', origin:'Memphis, TN',      destination:'Miami, FL',
    events:[] },
  { id:'SHP-005', carrier:'UPS',   tracking:'1Z999AA10123456785',   status:'exception',        item:'Avery Dennison SF100 — Gloss Red (2 rolls)',   ordered:'2026-04-10', shipped:'2026-04-11', eta:'2026-04-14', delivered:null,          supplier:'Vinyl Express',     service:'UPS 2-Day Air', weight:'7.8 lbs',  origin:'Houston, TX',      destination:'Miami, FL',
    events:[{date:'2026-04-14',time:'9:00 AM',location:'Miami, FL',            desc:'Delivery attempted — recipient unavailable'},{date:'2026-04-13',time:'4:20 PM',location:'Miami, FL',           desc:'Out for delivery'},{date:'2026-04-12',time:'1:00 AM',location:'Miami, FL',            desc:'Arrived at facility'},{date:'2026-04-11',time:'3:30 PM',location:'Houston, TX',           desc:'Picked up'}] },
];

function ptDetectCarrier(raw) {
  const n = raw.trim().replace(/\s+/g, '');
  if (/^1Z[A-Z0-9]{16}$/i.test(n))            return 'UPS';
  if (/^TBA\d+$/i.test(n))                     return 'Amazon';
  if (/^9[0-5]\d{18,20}$/.test(n))             return 'USPS';
  if (/^\d{10}$/.test(n))                      return 'DHL';
  if (/^\d{12}$|^\d{15}$|^\d{20,22}$/.test(n)) return 'FedEx';
  return null;
}

function ptMockLookup(carrier, tracking) {
  const base     = new Date('2026-04-15');
  const offsetDay = d => new Date(base.getTime() + d * 86400000).toISOString().slice(0, 10);
  const randTime  = () => { const h = 8 + Math.floor(Math.random() * 10); const m = [0,15,30,45][Math.floor(Math.random()*4)]; return `${h}:${String(m).padStart(2,'0')} ${h < 12 ? 'AM' : 'PM'}`; };
  const origins   = { UPS:'Houston, TX', FedEx:'Memphis, TN', USPS:'Chicago, IL', DHL:'Cincinnati, OH', Amazon:'Phoenix, AZ' };
  const svcMap    = { UPS:['UPS Ground','UPS 2-Day Air','UPS Next Day Air'], FedEx:['FedEx Ground','FedEx 2Day','FedEx Overnight'], USPS:['USPS Priority Mail','USPS First-Class','USPS Express Mail'], DHL:['DHL Express','DHL eCommerce'], Amazon:['Amazon Standard','Amazon Prime'] };
  const etaDays   = { UPS:2, FedEx:3, USPS:1, DHL:4, Amazon:2 }[carrier] ?? 3;
  const svcList   = svcMap[carrier] || ['Standard'];
  return {
    carrier, tracking,
    status:      'in_transit',
    service:     svcList[Math.floor(Math.random() * svcList.length)],
    weight:      `${(Math.random() * 20 + 2).toFixed(1)} lbs`,
    origin:      origins[carrier] || 'Origin City',
    destination: 'Miami, FL',
    shipped:     offsetDay(-2),
    eta:         offsetDay(etaDays),
    delivered:   null,
    events: [
      { date: offsetDay(-1), time: randTime(), location: 'Dallas, TX',              desc: 'In transit — on the way' },
      { date: offsetDay(-2), time: randTime(), location: origins[carrier] || 'Origin', desc: 'Departed carrier facility' },
      { date: offsetDay(-2), time: randTime(), location: origins[carrier] || 'Origin', desc: 'Package picked up' },
    ],
  };
}

function ptCountdown(etaStr) {
  if (!etaStr) return null;
  const ms = new Date(etaStr + 'T23:59:00') - new Date();
  if (ms <= 0)                          return { label: 'Today', cls: 'text-red-600 dark:text-red-400 font-bold' };
  const days  = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  if (days === 0) return { label: `${hours}h`,             cls: 'text-amber-600 dark:text-amber-400 font-semibold' };
  if (days === 1) return { label: `Tomorrow`,              cls: 'text-amber-500 dark:text-amber-400 font-semibold' };
  return           { label: `${days} days`,               cls: 'text-[#2E8BF0] font-medium' };
}

function ptFmt(d) {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Waterfall of logo sources per carrier — tried in order until one loads.
// UPS + FedEx: confirmed working Wikipedia Commons paths.
// USPS / DHL / Amazon: multiple candidates cover possible hash variants + Clearbit fallback.
const PT_CARRIER_LOGO_URLS = {
  UPS: [
    'https://upload.wikimedia.org/wikipedia/commons/1/1b/UPS_Logo_Shield_2017.svg',
  ],
  FedEx: [
    'https://upload.wikimedia.org/wikipedia/commons/b/b9/FedEx_Corporation_-_2016_Logo.svg',
  ],
  USPS: [
    'https://upload.wikimedia.org/wikipedia/commons/3/38/USPS_Eagle_Logo_2.svg',
    'https://upload.wikimedia.org/wikipedia/en/c/c2/US-PostalService-Eagle-Logo.svg',
    'https://upload.wikimedia.org/wikipedia/commons/thumb/3/38/USPS_Eagle_Logo_2.svg/200px-USPS_Eagle_Logo_2.svg.png',
    'https://logo.clearbit.com/usps.com',
  ],
  DHL: [
    'https://upload.wikimedia.org/wikipedia/commons/a/a3/DHL_Logo.svg',
    'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a3/DHL_Logo.svg/200px-DHL_Logo.svg.png',
    'https://logo.clearbit.com/dhl.com',
  ],
  Amazon: [
    'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
    'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/200px-Amazon_logo.svg.png',
    'https://logo.clearbit.com/amazon.com',
  ],
};

// Inline SVG brand-mark fallbacks (used when external image fails to load)
function PtCarrierLogoFallback({ carrier }) {
  switch (carrier) {
    case 'UPS':
      return (
        <svg width="54" height="30" viewBox="0 0 54 30" xmlns="http://www.w3.org/2000/svg">
          <rect width="54" height="30" rx="4" fill="#351C15"/>
          <text x="27" y="20" textAnchor="middle" fontFamily="'Arial Black',Arial,sans-serif" fontWeight="900" fontSize="13" fill="white" letterSpacing="1.5">UPS</text>
        </svg>
      );
    case 'FedEx':
      return (
        <svg width="72" height="28" viewBox="0 0 72 28" xmlns="http://www.w3.org/2000/svg">
          <text x="0" y="22" fontFamily="Arial,sans-serif" fontWeight="900" fontSize="22" fill="#4D148C">Fed</text>
          <text x="41" y="22" fontFamily="Arial,sans-serif" fontWeight="900" fontSize="22" fill="#FF6200">Ex</text>
        </svg>
      );
    case 'USPS':
      return (
        <svg width="70" height="30" viewBox="0 0 70 30" xmlns="http://www.w3.org/2000/svg">
          <rect width="70" height="30" rx="4" fill="#004B97"/>
          <text x="35" y="20" textAnchor="middle" fontFamily="'Arial Black',Arial,sans-serif" fontWeight="900" fontSize="12" fill="white" letterSpacing="2.5">USPS</text>
        </svg>
      );
    case 'DHL':
      return (
        <svg width="58" height="30" viewBox="0 0 58 30" xmlns="http://www.w3.org/2000/svg">
          <rect width="58" height="30" rx="4" fill="#FFCC00"/>
          <text x="29" y="21" textAnchor="middle" fontFamily="'Arial Black',Arial,sans-serif" fontWeight="900" fontSize="15" fill="#D40511" letterSpacing="2.5">DHL</text>
        </svg>
      );
    case 'Amazon':
      return (
        <svg width="80" height="30" viewBox="0 0 80 30" xmlns="http://www.w3.org/2000/svg">
          <text x="0" y="19" fontFamily="Arial,sans-serif" fontWeight="700" fontSize="17" fill="#232F3E">amazon</text>
          <path d="M5 25 Q30 33 62 25" stroke="#FF9900" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          <path d="M59 23 L65 25 L59 27" fill="#FF9900"/>
        </svg>
      );
    default: {
      const style = PT_CARRIER_STYLE[carrier] || { bg: 'bg-gray-500', text: 'text-white', label: carrier };
      return <span className={`text-[var(--wm-text-caption)] font-black px-1.5 py-0.5 rounded ${style.bg} ${style.text}`}>{style.label}</span>;
    }
  }
}

// Tries each URL in the waterfall; falls through to inline SVG brand mark if all fail
function PtCarrierLogo({ carrier }) {
  const urls = PT_CARRIER_LOGO_URLS[carrier] || [];
  const [idx, setIdx] = useState(0);
  if (idx >= urls.length) return <PtCarrierLogoFallback carrier={carrier} />;
  return (
    <img
      key={idx}
      src={urls[idx]}
      alt={carrier}
      onError={() => setIdx(i => i + 1)}
      className="h-8 w-auto max-w-[100px] object-contain"
    />
  );
}

const PT_SORT_COLS = ['carrier','item','status','shipped','eta'];

function SortTh({ col, children, className = '', sort, onSort }) {
  const active = sort.key === col;
  return (
    <th
      onClick={() => onSort(col)}
      className={`px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap cursor-pointer select-none transition-colors
        ${active ? 'text-[#2E8BF0]' : 'text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE]'}
        ${className}`}
    >
      <span className="flex items-center gap-1">
        {children}
        {active ? (
          <svg className="w-3 h-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d={sort.dir === 'asc' ? 'M4.5 15.75l7.5-7.5 7.5 7.5' : 'M19.5 8.25l-7.5 7.5-7.5-7.5'} />
          </svg>
        ) : (
          <svg className="w-3 h-3 flex-shrink-0 opacity-25" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9" />
          </svg>
        )}
      </span>
    </th>
  );
}

function ParcelTrackingView() {
  const [shipments, setShipments] = useState(PT_INIT);
  const [filter,    setFilter]    = useState('all');
  const [search,    setSearch]    = useState('');
  const [sort,      setSort]      = useState({ key: 'eta', dir: 'asc' });
  const [expanded,  setExpanded]  = useState({});

  // Modal
  const [modal,       setModal]       = useState(false);
  const [trackingVal, setTrackingVal] = useState('');
  const [itemDesc,    setItemDesc]    = useState('');
  const [supplier,    setSupplier]    = useState('');
  const [lookupPhase, setLookupPhase] = useState('idle'); // idle|loading|found
  const [lookupData,  setLookupData]  = useState(null);
  const modalRef = useRef(null);

  const detectedCarrier = ptDetectCarrier(trackingVal);

  const openModal = () => { setModal(true); setTrackingVal(''); setItemDesc(''); setSupplier(''); setLookupPhase('idle'); setLookupData(null); };
  const closeModal = () => setModal(false);

  useEffect(() => {
    if (!modal) return;
    const h = e => { if (modalRef.current && !modalRef.current.contains(e.target)) closeModal(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [modal]);

  const handleLookup = () => {
    if (!detectedCarrier || !trackingVal.trim()) return;
    setLookupPhase('loading');
    setTimeout(() => { setLookupData(ptMockLookup(detectedCarrier, trackingVal.trim())); setLookupPhase('found'); }, 1600);
  };

  const handleAdd = () => {
    if (!lookupData) return;
    setShipments(prev => [{
      id:          `SHP-${String(prev.length + 1).padStart(3,'0')}`,
      carrier:     lookupData.carrier,
      tracking:    lookupData.tracking,
      status:      lookupData.status,
      item:        itemDesc || `Shipment ${lookupData.tracking}`,
      ordered:     new Date().toISOString().slice(0,10),
      shipped:     lookupData.shipped,
      eta:         lookupData.eta,
      delivered:   lookupData.delivered,
      supplier:    supplier || 'Unknown Supplier',
      service:     lookupData.service,
      weight:      lookupData.weight,
      origin:      lookupData.origin,
      destination: lookupData.destination,
      events:      lookupData.events,
    }, ...prev]);
    closeModal();
  };

  const toggleSort = key => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const rows = useMemo(() => {
    let list = shipments.filter(s => {
      if (filter !== 'all' && s.status !== filter) return false;
      const q = search.toLowerCase();
      return !q || s.item.toLowerCase().includes(q) || s.tracking.toLowerCase().includes(q) || (s.supplier || '').toLowerCase().includes(q) || s.carrier.toLowerCase().includes(q);
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      switch (sort.key) {
        case 'carrier':  return a.carrier.localeCompare(b.carrier) * dir;
        case 'item':     return a.item.localeCompare(b.item) * dir;
        case 'status':   return a.status.localeCompare(b.status) * dir;
        case 'shipped':  return ((a.shipped||'0000').localeCompare(b.shipped||'0000')) * dir;
        case 'eta':      return ((a.eta||'9999').localeCompare(b.eta||'9999')) * dir;
        default:         return 0;
      }
    });
    return list;
  }, [shipments, filter, search, sort]);

  const counts = Object.fromEntries(
    ['all','ordered','in_transit','out_for_delivery','delivered','exception'].map(k => [
      k, k === 'all' ? shipments.length : shipments.filter(s => s.status === k).length,
    ])
  );

  return (
    <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

      {/* Top bar */}
      <div className="h-10 bg-white dark:bg-[#1B2A3E] border-b border-gray-200 dark:border-[#243348] flex items-center px-6 gap-3 flex-shrink-0">
        <h1 className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Parcel Tracking</h1>
        <span className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE]">Material orders &amp; inbound shipments</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#64748B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z" />
            </svg>
            <input type="text" placeholder="Search shipments…" value={search} onChange={e => setSearch(e.target.value)}
              className="h-7 pl-7 pr-3 w-52 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[#64748B] focus:outline-none focus:border-[#2E8BF0] transition-colors" />
          </div>
          <button onClick={openModal} className="wm-btn-primary">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
            Add Shipment
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 bg-gray-50 dark:bg-[#0F1923]">
        <div className="max-w-7xl mx-auto space-y-4">

          {/* Status filter tiles */}
          <div className="grid grid-cols-5 gap-3">
            {['ordered','in_transit','out_for_delivery','delivered','exception'].map(key => {
              const cfg = PT_STATUS[key];
              return (
                <button key={key} onClick={() => setFilter(f => f === key ? 'all' : key)}
                  className={`rounded-lg border p-3 text-left transition-all ${filter === key ? `border-current ${cfg.bg} ${cfg.color}` : 'border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] hover:border-gray-300 dark:hover:border-[#364860]'}`}>
                  <p className={`text-[var(--wm-text-hero)] font-bold mb-0.5 ${filter === key ? cfg.color : 'text-[#0F1923] dark:text-[#F8FAFE]'}`}>{counts[key]}</p>
                  <p className={`text-[var(--wm-text-caption)] font-medium ${filter === key ? cfg.color : 'text-[#64748B] dark:text-[#7D93AE]'}`}>{cfg.label}</p>
                </button>
              );
            })}
          </div>

          {/* Table */}
          <div className="rounded-xl border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[var(--wm-text-ui)] border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-[#243348] bg-gray-50 dark:bg-[#0F1923]/60">
                    <SortTh col="carrier"  className="pl-5 w-[120px]"  sort={sort} onSort={toggleSort}>Carrier</SortTh>
                    <SortTh col="item"     className="min-w-[220px]"   sort={sort} onSort={toggleSort}>Item</SortTh>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#7D93AE] w-[160px]">Tracking #</th>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#7D93AE] w-[130px]">Supplier</th>
                    <SortTh col="status"   className="w-[140px]"       sort={sort} onSort={toggleSort}>Status</SortTh>
                    <SortTh col="shipped"  className="w-[90px]"        sort={sort} onSort={toggleSort}>Shipped</SortTh>
                    {/* ETA — highlighted column */}
                    <SortTh col="eta"      className="w-[160px] bg-[#2E8BF0]/8 dark:bg-[#2E8BF0]/10 text-[#2E8BF0] border-x border-[#2E8BF0]/15 dark:border-[#2E8BF0]/20" sort={sort} onSort={toggleSort}>ETA ↓</SortTh>
                    <th className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[#64748B] dark:text-[#7D93AE] w-[90px] pr-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="py-16 text-center text-[var(--wm-text-body)] text-[#64748B] dark:text-[#7D93AE]">No shipments match your filter.</td></tr>
                  )}
                  {rows.map((s, ri) => {
                    const cfg      = PT_STATUS[s.status];
                    const countdown = s.status !== 'delivered' && s.status !== 'exception' ? ptCountdown(s.eta) : null;
                    const isOpen   = !!expanded[s.id];
                    const rowBg    = ri % 2 === 1 ? 'bg-gray-50/60 dark:bg-[#0F1923]/20' : '';
                    return (
                      <>
                        <tr key={s.id} className={`${rowBg} border-b border-gray-100 dark:border-[#243348] last:border-0 hover:bg-blue-50/40 dark:hover:bg-blue-900/10 transition-colors`}>

                          {/* Carrier */}
                          <td className="pl-5 pr-3 py-3 align-middle">
                            <PtCarrierLogo carrier={s.carrier} />
                          </td>

                          {/* Item */}
                          <td className="px-3 py-3 align-middle">
                            <p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] leading-snug line-clamp-2">{s.item}</p>
                            {s.service && <p className="text-[var(--wm-text-micro)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">{s.service}{s.weight ? ` · ${s.weight}` : ''}</p>}
                          </td>

                          {/* Tracking */}
                          <td className="px-3 py-3 align-middle">
                            <span className="font-mono text-[var(--wm-text-caption)] text-[#0F1923] dark:text-[#F8FAFE] break-all">{s.tracking}</span>
                          </td>

                          {/* Supplier */}
                          <td className="px-3 py-3 align-middle text-[#64748B] dark:text-[#7D93AE]">{s.supplier}</td>

                          {/* Status */}
                          <td className="px-3 py-3 align-middle">
                            <span className={`inline-flex items-center gap-1 text-[var(--wm-text-caption)] font-semibold px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                              {cfg.label}
                            </span>
                          </td>

                          {/* Shipped */}
                          <td className="px-3 py-3 align-middle text-[#64748B] dark:text-[#7D93AE]">{ptFmt(s.shipped)}</td>

                          {/* ETA — highlighted */}
                          <td className="px-4 py-3 align-middle bg-[#2E8BF0]/5 dark:bg-[#2E8BF0]/8 border-x border-[#2E8BF0]/12 dark:border-[#2E8BF0]/15">
                            {s.status === 'delivered' ? (
                              <div>
                                <p className="text-[var(--wm-text-body)] font-bold text-emerald-600 dark:text-emerald-400">✓ Delivered</p>
                                <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">{ptFmt(s.delivered)}</p>
                              </div>
                            ) : s.status === 'exception' ? (
                              <div>
                                <p className="text-[var(--wm-text-body)] font-bold text-red-600 dark:text-red-400">⚠ Exception</p>
                                <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">Was {ptFmt(s.eta)}</p>
                              </div>
                            ) : (
                              <div>
                                <p className="text-[var(--wm-text-hero)] font-bold text-[#0F1923] dark:text-[#F8FAFE] leading-tight">{ptFmt(s.eta)}</p>
                                {countdown && <p className={`text-[var(--wm-text-body)] mt-1 ${countdown.cls}`}>{countdown.label}</p>}
                              </div>
                            )}
                          </td>

                          {/* Actions */}
                          <td className="px-3 py-3 pr-5 align-middle">
                            <div className="flex items-center gap-2">
                              {s.events?.length > 0 && (
                                <button onClick={() => setExpanded(p => ({...p, [s.id]: !p[s.id]}))}
                                  title={isOpen ? 'Hide history' : 'Show history'}
                                  className="text-[#64748B] dark:text-[#7D93AE] hover:text-[#2E8BF0] transition-colors">
                                  <svg className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                  </svg>
                                </button>
                              )}
                              {PT_CARRIER_URLS[s.carrier] && (
                                <a href={`${PT_CARRIER_URLS[s.carrier]}${s.tracking}`} target="_blank" rel="noopener noreferrer"
                                  title="Open on carrier website"
                                  className="text-[#64748B] dark:text-[#7D93AE] hover:text-[#2E8BF0] transition-colors">
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                  </svg>
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>

                        {/* Expanded event history row */}
                        {isOpen && s.events?.length > 0 && (
                          <tr key={`${s.id}-events`} className={`${rowBg} border-b border-gray-100 dark:border-[#243348]`}>
                            <td colSpan={8} className="px-5 pb-4 pt-0">
                              <div className="ml-[88px] border-l-2 border-gray-200 dark:border-[#243348] pl-4 space-y-2.5 pt-1">
                                {s.events.map((ev, i) => (
                                  <div key={i} className="relative flex gap-3">
                                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-white dark:bg-[#1B2A3E] border-2 border-gray-300 dark:border-[#364860]" />
                                    <div>
                                      <p className="text-[var(--wm-text-caption)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">{ev.desc}</p>
                                      <p className="text-[var(--wm-text-micro)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">{ev.location} &middot; {ptFmt(ev.date)} at {ev.time}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Shipment Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div ref={modalRef} className="w-full max-w-md bg-white dark:bg-[#1B2A3E] rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-[#243348]">
              <div>
                <p className="text-[var(--wm-text-heading)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">Track a New Shipment</p>
                <p className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] mt-0.5">Enter a tracking number to pull carrier data automatically</p>
              </div>
              <button onClick={closeModal} className="w-7 h-7 rounded-full flex items-center justify-center text-[#64748B] hover:bg-gray-100 dark:hover:bg-[#243348] transition-colors">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-[var(--wm-text-caption)] font-semibold text-[#64748B] dark:text-[#7D93AE] uppercase tracking-wider mb-1.5">Tracking Number</label>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input type="text" placeholder="e.g. 1Z999AA10123456784" value={trackingVal}
                      onChange={e => { setTrackingVal(e.target.value); setLookupPhase('idle'); setLookupData(null); }}
                      onKeyDown={e => e.key === 'Enter' && handleLookup()}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[#64748B] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0] transition-colors font-mono" />
                    {detectedCarrier && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        <PtCarrierLogo carrier={detectedCarrier} />
                      </span>
                    )}
                  </div>
                  <button onClick={handleLookup} disabled={!detectedCarrier || lookupPhase === 'loading'}
                    className="h-9 px-4 rounded-lg bg-[#2E8BF0] text-white text-[var(--wm-text-ui)] font-medium hover:bg-[#1a6fd4] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors flex-shrink-0">
                    {lookupPhase === 'loading'
                      ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                      : <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/></svg>}
                    {lookupPhase === 'loading' ? 'Looking up…' : 'Look Up'}
                  </button>
                </div>
                {!detectedCarrier && trackingVal.length > 3 && (
                  <p className="mt-1.5 text-[var(--wm-text-caption)] text-amber-600 dark:text-amber-400">Carrier not recognized — try UPS (1Z…), FedEx (12–15 digits), USPS (94…), or DHL (10 digits)</p>
                )}
              </div>

              {lookupPhase === 'found' && lookupData && (
                <div className="rounded-xl border border-[#2E8BF0]/30 bg-blue-50 dark:bg-blue-900/10 p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <PtCarrierLogo carrier={lookupData.carrier} />
                    <span className="text-[var(--wm-text-ui)] font-semibold text-[#0F1923] dark:text-[#F8FAFE]">{lookupData.service}</span>
                    <span className={`ml-auto text-[var(--wm-text-caption)] font-semibold px-2 py-0.5 rounded-full ${PT_STATUS[lookupData.status]?.bg} ${PT_STATUS[lookupData.status]?.color}`}>{PT_STATUS[lookupData.status]?.label}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-[var(--wm-text-caption)]">
                    <div><p className="text-[#64748B] dark:text-[#7D93AE]">Origin</p><p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] mt-0.5">{lookupData.origin}</p></div>
                    <div><p className="text-[#64748B] dark:text-[#7D93AE]">Destination</p><p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] mt-0.5">{lookupData.destination}</p></div>
                    <div><p className="text-[#64748B] dark:text-[#7D93AE]">Shipped</p><p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] mt-0.5">{ptFmt(lookupData.shipped)}</p></div>
                    <div><p className="text-[#64748B] dark:text-[#7D93AE]">Est. Delivery</p><p className="font-bold text-[#0F1923] dark:text-[#F8FAFE] text-[var(--wm-text-body)] mt-0.5">{ptFmt(lookupData.eta)}</p></div>
                    <div><p className="text-[#64748B] dark:text-[#7D93AE]">Weight</p><p className="font-medium text-[#0F1923] dark:text-[#F8FAFE] mt-0.5">{lookupData.weight}</p></div>
                    <div><p className="text-[#64748B] dark:text-[#7D93AE]">Countdown</p><p className={`font-semibold mt-0.5 ${ptCountdown(lookupData.eta)?.cls || 'text-[#2E8BF0]'}`}>{ptCountdown(lookupData.eta)?.label || '—'}</p></div>
                  </div>
                  {lookupData.events?.length > 0 && (
                    <div className="border-t border-[#2E8BF0]/20 pt-2 space-y-1.5">
                      {lookupData.events.slice(0,3).map((ev,i) => (
                        <div key={i} className="flex gap-2 text-[var(--wm-text-caption)]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#2E8BF0] flex-shrink-0 mt-1" />
                          <div><span className="font-medium text-[#0F1923] dark:text-[#F8FAFE]">{ev.desc}</span><span className="text-[#64748B] dark:text-[#7D93AE]"> · {ev.location}, {ptFmt(ev.date)}</span></div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {lookupPhase === 'found' && (
                <>
                  <div>
                    <label className="block text-[var(--wm-text-caption)] font-semibold text-[#64748B] dark:text-[#7D93AE] uppercase tracking-wider mb-1.5">Item Description</label>
                    <input type="text" placeholder="e.g. Avery Dennison SW900 Matte Black — 3 rolls" value={itemDesc} onChange={e => setItemDesc(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[#64748B] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0] transition-colors" />
                  </div>
                  <div>
                    <label className="block text-[var(--wm-text-caption)] font-semibold text-[#64748B] dark:text-[#7D93AE] uppercase tracking-wider mb-1.5">Supplier</label>
                    <input type="text" placeholder="e.g. Wrap Supply Co." value={supplier} onChange={e => setSupplier(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#0F1923] text-[var(--wm-text-ui)] text-[#0F1923] dark:text-[#F8FAFE] placeholder:text-[#64748B] focus:outline-none focus:border-[#2E8BF0] focus:ring-1 focus:ring-[#2E8BF0] transition-colors" />
                  </div>
                </>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-100 dark:border-[#243348] flex justify-end gap-2">
              <button onClick={closeModal} className="h-8 px-4 rounded-lg border border-gray-200 dark:border-[#243348] text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors">Cancel</button>
              {lookupPhase === 'found' && (
                <button onClick={handleAdd} className="wm-btn-primary">Add Shipment</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaceholderView({ view }) {
  const meta = VIEW_META[view] || { label: view, subtitle: '' };
  return (
    <div className="flex-1 flex flex-col">
      <div className="h-10 bg-white dark:bg-[#1B2A3E] border-b border-gray-200 dark:border-[#243348] flex items-center px-6 flex-shrink-0">
        <span className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">{meta.label}</span>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-white dark:bg-[#1B2A3E] flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-[#64748B] dark:text-[#7D93AE]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">{meta.label}</p>
          <p className="text-[var(--wm-text-meta)] text-[#64748B] dark:text-[#7D93AE] mt-1">{meta.subtitle}</p>
          <span className="inline-block mt-3 px-2.5 py-1 rounded-full bg-white dark:bg-[#1B2A3E] text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">Coming soon</span>
        </div>
      </div>
    </div>
  );
}

// ─── XP Ring widget ──────────────────────────────────────────────────────────

function XPRing({ onNavigate }) {
  const { currentEmployeeId, employees, getEmployeeStats } = useGamification();
  const [hovered, setHovered] = useState(false);

  const employee = employees.find(e => e.id === currentEmployeeId) || employees[0];
  if (!employee) return null;

  const stats = getEmployeeStats(employee.id);
  const { totalXP, level, nextLevel, xpToNext, progressPct } = stats;

  // SVG ring math
  const r = 16;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference * (1 - progressPct / 100);

  const xpLabel = totalXP >= 1000
    ? `${(totalXP / 1000).toFixed(1).replace(/\.0$/, '')}k`
    : `${totalXP}`;

  return (
    <div className="relative" onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <button
        onClick={() => onNavigate('performance')}
        title={`${employee.name} · ${level.title} · ${totalXP} XP`}
        className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-[#243348] transition-colors"
      >
        {/* SVG progress ring */}
        <div className="relative w-9 h-9 flex-shrink-0">
          <svg className="w-9 h-9 -rotate-90" viewBox="0 0 40 40">
            {/* Track */}
            <circle
              cx="20" cy="20" r={r}
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              className="text-gray-200 dark:text-[#243348]"
            />
            {/* Progress arc */}
            <circle
              cx="20" cy="20" r={r}
              fill="none"
              stroke={level.color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
          </svg>
          {/* Avatar in center */}
          <div
            className="absolute inset-0 flex items-center justify-center rounded-full"
            style={{ background: `${employee.color}18` }}
          >
            <span className="text-[var(--wm-text-micro)] font-bold leading-none" style={{ color: employee.color }}>
              {employee.initials}
            </span>
          </div>
        </div>

        {/* XP count + label */}
        <div className="hidden sm:flex flex-col items-start leading-none gap-0.5">
          <span className="text-[var(--wm-text-ui)] font-bold tabular-nums font-mono" style={{ color: level.color }}>
            {xpLabel} <span className="font-medium opacity-60">XP</span>
          </span>
          <span className="text-[var(--wm-text-micro)] font-medium uppercase tracking-wide text-[#64748B] dark:text-[#7D93AE]">
            {level.title}
          </span>
        </div>
      </button>

      {/* Hover tooltip */}
      {hovered && (
        <div className="absolute right-0 top-11 z-50 w-52 wm-glass rounded-lg shadow-xl p-3 pointer-events-none" style={{ background: 'var(--wm-glass-bg)', border: '1px solid var(--wm-glass-border)', boxShadow: '0 20px 60px rgba(0,0,0,0.22), inset 0 1px 0 var(--wm-glass-specular)' }}>
          <div className="flex items-center gap-2 mb-2.5">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${employee.color}20` }}>
              <span className="text-[var(--wm-text-caption)] font-bold" style={{ color: employee.color }}>{employee.initials}</span>
            </div>
            <div>
              <p className="text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE]">{employee.name}</p>
              <p className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE]">{employee.role}</p>
            </div>
          </div>
          {/* Level badge */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ backgroundColor: `${level.color}20`, color: level.color }}>
              {level.title}
            </span>
            <span className="text-[var(--wm-text-ui)] font-mono font-bold" style={{ color: level.color }}>{totalXP.toLocaleString()} XP</span>
          </div>
          {/* Progress bar */}
          <div className="h-1.5 rounded-full bg-gray-100 dark:bg-[#243348] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${progressPct}%`, backgroundColor: level.color }} />
          </div>
          {nextLevel ? (
            <p className="text-[var(--wm-text-caption)] text-[#64748B] dark:text-[#7D93AE] mt-1.5 text-right">
              {xpToNext.toLocaleString()} XP to <span className="font-medium" style={{ color: nextLevel.color }}>{nextLevel.title}</span>
            </p>
          ) : (
            <p className="text-[var(--wm-text-caption)] text-center mt-1.5 font-semibold" style={{ color: level.color }}>MAX LEVEL</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Company logo (TopBar) ────────────────────────────────────────────────────

/** Measure the average luminance (0–255) of non-transparent pixels in an image src. */
function measureLuminance(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = img.naturalWidth  || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let sum = 0, count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3];
          if (alpha > 20) { // skip near-transparent pixels
            sum += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
            count++;
          }
        }
        resolve(count > 0 ? sum / count : 128);
      } catch {
        resolve(128); // CORS or tainted-canvas fallback
      }
    };
    img.onerror = () => resolve(128);
    img.src = src;
  });
}

function CompanyLogo({ onNavigate }) {
  const [logo, setLogo]           = useState(() => localStorage.getItem('wm-shop-logo') || null);
  const [luminance, setLuminance] = useState(128); // 0=black … 255=white
  const { mode } = useTheme();

  // Keep in sync when Settings updates the logo in another render
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'wm-shop-logo') setLogo(e.newValue || null);
    };
    window.addEventListener('storage', onStorage);
    const id = setInterval(() => {
      const current = localStorage.getItem('wm-shop-logo');
      setLogo(prev => (prev !== current ? current : prev));
    }, 1500);
    return () => { window.removeEventListener('storage', onStorage); clearInterval(id); };
  }, []);

  // Re-measure luminance whenever the logo changes
  useEffect(() => {
    if (!logo) return;
    measureLuminance(logo).then(setLuminance);
  }, [logo]);

  if (!logo) return null;

  // Dark mode  + dark logo  (lum < 100) → invert so it reads as light
  // Light mode + light logo (lum > 200) → invert so it reads as dark
  const isDark  = mode === 'dark';
  const needsInvert = (isDark && luminance < 100) || (!isDark && luminance > 200);
  const imgFilter = needsInvert ? 'invert(1)' : undefined;

  return (
    <button
      onClick={() => onNavigate('settings')}
      title="Company logo — click to manage"
      className="flex items-center mr-3 flex-shrink-0 group"
    >
      <img
        src={logo}
        alt="Company logo"
        className="h-8 max-w-[140px] object-contain rounded opacity-90 group-hover:opacity-100 transition-all"
        style={{ filter: imgFilter }}
      />
    </button>
  );
}

// ─── Account dropdown (TopBar) ───────────────────────────────────────────────

function AccountDropdown({ onNavigate, onLogout }) {
  const { currentRole } = useRoles();
  const { isWmStaff, wmStaffMember, wmCan } = useWmStaff();
  // Senior staff display as Super Admin regardless of the shop's stored role
  const isSeniorStaff = wmCan('internal.flags');
  const role = isSeniorStaff ? ROLES['superadmin'] : ROLES[currentRole];
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const ref = useRef(null);

  // XP row — reuses same ring math as the XPRing component in the top bar
  const { xpEnabled } = useFeatureFlags();
  const { currentEmployeeId, employees, getEmployeeStats } = useGamification();
  const xpEmployee = xpEnabled ? (employees.find(e => e.id === currentEmployeeId) || employees[0] || null) : null;
  const xpStats    = xpEmployee ? getEmployeeStats(xpEmployee.id) : null;
  const XP_R = 16; // ring radius (px, matches XPRing component)
  const XP_CIRC = 2 * Math.PI * XP_R;

  // Read user profile (personal) first, fall back to shop profile
  const [profile, setProfile] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
      const shop = JSON.parse(localStorage.getItem('wm-shop-profile') || '{}');
      return { ...shop, ...user };
    } catch { return {}; }
  });
  useEffect(() => {
    const sync = () => {
      try {
        const user = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
        const shop = JSON.parse(localStorage.getItem('wm-shop-profile') || '{}');
        setProfile({ ...shop, ...user });
      } catch { /* no-op */ }
    };
    window.addEventListener('storage', sync);
    const id = setInterval(sync, 2000);
    return () => { window.removeEventListener('storage', sync); clearInterval(id); };
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const shopName   = profile.displayName || profile.name || 'WrapMind User';
  const email      = profile.personalEmail || profile.email || 'admin@wrapmind.app';
  const initials   = shopName.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'WM';
  const roleColor  = role?.color || '#2563eb';
  const avatarUrl  = profile.avatarUrl || null;
  // avatarColor comes from user profile settings; falls back to role colour so the ring stays consistent
  const avatarColor = profile.avatarColor || roleColor;

  const go = (view, data) => { setOpen(false); onNavigate(view, data); };

  const menuItems = [
    {
      id: 'user-profile',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
        </svg>
      ),
      label: 'My Profile',
      sub: 'Name, avatar, personal email',
      action: () => go('settings', { settingsTab: 'user-profile' }),
    },
    {
      id: 'notifications',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
      ),
      label: 'Notifications',
      sub: 'Alerts and digests',
      action: () => go('notifications', null),
    },
    {
      id: 'appearance',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="w-4 h-4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.098 19.902a3.75 3.75 0 005.304 0l6.401-6.402M6.75 21A3.75 3.75 0 013 17.25V4.125C3 3.504 3.504 3 4.125 3h5.25c.621 0 1.125.504 1.125 1.125v4.072M6.75 21a3.75 3.75 0 003.75-3.75V8.197M6.75 21h13.125c.621 0 1.125-.504 1.125-1.125v-5.25c0-.621-.504-1.125-1.125-1.125h-4.072M10.5 8.197l2.88-2.88c.438-.439 1.15-.439 1.59 0l3.712 3.713c.44.44.44 1.152 0 1.59l-2.879 2.88M6.75 17.25h.008v.008H6.75v-.008z" />
        </svg>
      ),
      label: 'Appearance & Features',
      sub: 'Themes, density, flags',
      action: () => go('settings', { settingsTab: 'appearance' }),
    },
  ];

  return (
    <div ref={ref} className="relative flex-shrink-0">
      {/* Trigger */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-2 h-8 pl-1.5 pr-2 rounded-lg transition-all duration-150 ${open ? 'bg-gray-100 dark:bg-[#243348]' : 'hover:bg-gray-100 dark:hover:bg-[#243348]'}`}
        title="Account menu"
      >
        {/* Gradient avatar ring */}
        <div className="relative w-6 h-6 flex-shrink-0">
          <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(from 135deg, ${avatarColor}, ${avatarColor}88, ${avatarColor})`, padding: 1.5 }}>
            <div className="w-full h-full rounded-full bg-white dark:bg-[#1B2A3E]" />
          </div>
          <div className="absolute inset-[2px] rounded-full flex items-center justify-center text-[var(--wm-text-micro)] font-bold text-white overflow-hidden"
            style={{ background: avatarUrl ? 'transparent' : `linear-gradient(135deg, ${avatarColor}dd, ${avatarColor})` }}>
            {avatarUrl ? <img src={avatarUrl} alt={shopName} className="w-full h-full object-cover rounded-full" /> : initials}
          </div>
        </div>
        <div className="hidden sm:flex flex-col items-start leading-none">
          <span className="text-[var(--wm-text-meta)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] max-w-[88px] truncate">{shopName}</span>
          <span className="text-[var(--wm-text-micro)] font-medium mt-0.5" style={{ color: isWmStaff ? '#F59E0B' : roleColor }}>
            {isWmStaff && wmStaffMember ? `★ ${wmStaffMember.name}` : (role?.label || 'Admin')}
          </span>
        </div>
        <svg className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Panel */}
      {open && (
        <div
          className="absolute right-0 top-full mt-1.5 z-50 overflow-hidden wm-glass"
          style={{
            width: 272,
            borderRadius: 14,
            border: '1px solid var(--wm-glass-border)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.22), 0 4px 16px rgba(0,0,0,0.12), inset 0 1px 0 var(--wm-glass-specular)',
            background: 'var(--wm-glass-bg-70)',
          }}>

          {/* Hero header */}
          <div className="relative overflow-hidden px-4 pt-4 pb-3"
            style={{ background: `linear-gradient(135deg, ${avatarColor}18 0%, ${avatarColor}06 60%, transparent 100%)` }}>
            {/* Decorative circles */}
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10"
              style={{ background: avatarColor }} />
            <div className="absolute -bottom-4 -right-2 w-14 h-14 rounded-full opacity-8"
              style={{ background: avatarColor }} />

            <div className="relative flex items-start gap-3">
              {/* Large avatar */}
              <div className="relative w-11 h-11 flex-shrink-0">
                <div className="absolute inset-0 rounded-xl" style={{ background: `linear-gradient(135deg, ${avatarColor}, ${avatarColor}88)`, padding: 1.5, borderRadius: 12 }}>
                  <div className="w-full h-full rounded-[10px] bg-white dark:bg-[#1B2A3E]" />
                </div>
                <div className="absolute inset-[2px] rounded-[10px] flex items-center justify-center text-[var(--wm-text-heading)] font-bold text-white overflow-hidden"
                  style={{ background: avatarUrl ? 'transparent' : `linear-gradient(135deg, ${avatarColor}ee, ${avatarColor}cc)` }}>
                  {avatarUrl ? <img src={avatarUrl} alt={shopName} className="w-full h-full object-cover" /> : initials}
                </div>
                {/* Online dot */}
                <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white dark:border-[#1B2A3E]" />
              </div>

              <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-[var(--wm-text-body)] font-semibold text-[#0F1923] dark:text-[#F8FAFE] truncate leading-tight">{shopName}</p>
                <p className="text-[var(--wm-text-meta)] text-gray-500 dark:text-gray-400 truncate mt-0.5 font-mono">{email}</p>
                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[var(--wm-text-caption)] font-semibold"
                    style={{ background: `${roleColor}18`, color: roleColor, border: `1px solid ${roleColor}30` }}>
                    <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 19.5h19.5M2.25 4.5l4.5 9 5.25-9 5.25 9 4.5-9" />
                    </svg>
                    {role?.label}
                  </span>
                  {isWmStaff && wmStaffMember && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[var(--wm-text-caption)] font-semibold"
                      style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.30)' }}>
                      <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                      </svg>
                      WM {wmStaffMember.name}
                    </span>
                  )}
                  <span className="text-[var(--wm-text-micro)] text-emerald-500 font-medium flex items-center gap-0.5">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    Active
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-100 dark:bg-gray-700/60" />

          {/* ── XP progress row (visible only when XP system is enabled) ───── */}
          {xpEnabled && xpEmployee && xpStats && (() => {
            const xpLabel = xpStats.totalXP >= 1000
              ? `${(xpStats.totalXP / 1000).toFixed(1).replace(/\.0$/, '')}k`
              : String(xpStats.totalXP);
            return (
              <>
                <button
                  onClick={() => go('performance')}
                  onMouseEnter={() => setHovered('xp')}
                  onMouseLeave={() => setHovered(null)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left transition-all duration-100"
                  style={{ background: hovered === 'xp' ? `${xpStats.level.color}0d` : 'transparent' }}
                  title={`${xpEmployee.name} · ${xpStats.level.title} · ${xpStats.totalXP} XP — click to view performance`}
                >
                  {/* Circular XP ring */}
                  <div className="relative w-9 h-9 flex-shrink-0">
                    <svg className="w-9 h-9 -rotate-90" viewBox="0 0 40 40">
                      <circle cx="20" cy="20" r={XP_R} fill="none" stroke="currentColor" strokeWidth="3"
                        className="text-gray-100 dark:text-[#243348]" />
                      <circle cx="20" cy="20" r={XP_R} fill="none" stroke={xpStats.level.color} strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray={XP_CIRC}
                        strokeDashoffset={XP_CIRC * (1 - xpStats.progressPct / 100)}
                        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center rounded-full"
                      style={{ background: `${xpEmployee.color}18` }}>
                      <span className="text-[var(--wm-text-micro)] font-bold leading-none" style={{ color: xpEmployee.color }}>
                        {xpEmployee.initials}
                      </span>
                    </div>
                  </div>

                  {/* Level title + XP count + linear progress bar */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[var(--wm-text-caption)] font-semibold uppercase tracking-wide"
                        style={{ color: xpStats.level.color }}>{xpStats.level.title}</span>
                      <span className="text-[var(--wm-text-meta)] font-mono font-bold"
                        style={{ color: xpStats.level.color }}>{xpLabel} <span className="opacity-60 font-medium">XP</span></span>
                    </div>
                    <div className="h-1 rounded-full bg-gray-100 dark:bg-[#243348] overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500"
                        style={{ width: `${xpStats.progressPct}%`, backgroundColor: xpStats.level.color }} />
                    </div>
                    {xpStats.nextLevel ? (
                      <p className="text-[var(--wm-text-micro)] text-gray-400 dark:text-gray-500 mt-1 leading-tight">
                        {xpStats.xpToNext.toLocaleString()} XP to{' '}
                        <span style={{ color: xpStats.nextLevel.color }}>{xpStats.nextLevel.title}</span>
                      </p>
                    ) : (
                      <p className="text-[var(--wm-text-micro)] font-semibold mt-1" style={{ color: xpStats.level.color }}>MAX LEVEL</p>
                    )}
                  </div>
                </button>
                <div className="h-px bg-gray-100 dark:bg-gray-700/60" />
              </>
            );
          })()}

          {/* Menu items */}
          <div className="py-1.5 px-1.5">
            {menuItems.map(item => (
              <button
                key={item.id}
                onClick={item.action}
                onMouseEnter={() => setHovered(item.id)}
                onMouseLeave={() => setHovered(null)}
                className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-all duration-100 group"
                style={{
                  background: hovered === item.id ? `${roleColor}0e` : 'transparent',
                }}>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-100"
                  style={{
                    background: hovered === item.id ? `${roleColor}18` : 'rgba(0,0,0,0.04)',
                    color: hovered === item.id ? roleColor : '#6b7280',
                  }}>
                  {item.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[var(--wm-text-ui)] font-semibold leading-tight"
                    style={{ color: hovered === item.id ? roleColor : 'var(--text-primary, #111827)' }}>
                    {item.label}
                  </p>
                  <p className="text-[var(--wm-text-caption)] text-gray-400 dark:text-gray-500 mt-0.5 leading-tight">{item.sub}</p>
                </div>
                <svg className="w-3 h-3 text-gray-300 flex-shrink-0 transition-all duration-100 group-hover:translate-x-0.5"
                  style={{ color: hovered === item.id ? `${roleColor}88` : undefined }}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>

          {/* Footer — sign out */}
          <div className="px-1.5 pb-1.5">
            <div className="h-px bg-gray-100 dark:bg-gray-700/60 mb-1.5" />
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              onMouseEnter={() => setHovered('logout')}
              onMouseLeave={() => setHovered(null)}
              className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition-all duration-100"
              style={{ background: hovered === 'logout' ? 'rgba(239,68,68,0.06)' : 'transparent' }}>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-100"
                style={{ background: hovered === 'logout' ? 'rgba(239,68,68,0.1)' : 'rgba(0,0,0,0.04)', color: hovered === 'logout' ? '#ef4444' : '#6b7280' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                </svg>
              </div>
              <span className="text-[var(--wm-text-ui)] font-semibold transition-colors duration-100"
                style={{ color: hovered === 'logout' ? '#ef4444' : '#6b7280' }}>
                Sign out
              </span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Greeting helpers ─────────────────────────────────────────────────────────

function getGreeting(firstName) {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return `Good morning, ${firstName} ☀️  Here's what's happening at the shop today.`;
  if (h >= 12 && h < 17) return `Good afternoon, ${firstName} 👋  Here's what's happening at the shop today.`;
  if (h >= 17 && h < 21) return `Good evening, ${firstName} 🌙  Here's what's happening at the shop today.`;
  return `Burning the midnight oil, ${firstName} 🔥  Here's what's on deck.`;
}

function GreetingBanner() {
  const [firstName, setFirstName] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
      const shop = JSON.parse(localStorage.getItem('wm-shop-profile') || '{}');
      const full = user.displayName || user.name || shop.name || '';
      return full.split(' ')[0] || 'there';
    } catch { return 'there'; }
  });

  useEffect(() => {
    const sync = () => {
      try {
        const user = JSON.parse(localStorage.getItem('wm-user-profile') || '{}');
        const shop = JSON.parse(localStorage.getItem('wm-shop-profile') || '{}');
        const full = user.displayName || user.name || shop.name || '';
        setFirstName(full.split(' ')[0] || 'there');
      } catch { /* no-op */ }
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  return (
    <div
      className="flex-shrink-0 flex items-center px-6 border-b border-gray-100 dark:border-[#243348] wm-fade-in-up"
      style={{ height: 34 }}
    >
      <p
        className="text-[11.5px] font-medium truncate"
        style={{ color: 'var(--text-muted, #64748B)' }}
      >
        {getGreeting(firstName)}
      </p>
    </div>
  );
}


// ─── Top control bar ─────────────────────────────────────────────────────────

function TopBar({ onNavigate, onLogout, onOpenPalette }) {
  const { multiLocationEnabled } = useFeatureFlags();
  const { unreadCount } = useNotifications();
  const { focusMode, setFocusMode } = useTheme();
  return (
    <div className="wm-glass h-[58px] flex items-center px-4 border-b sticky top-0 z-20 flex-shrink-0 relative" style={{ background: 'var(--wm-glass-bg)', borderColor: 'var(--wm-glass-border)', boxShadow: 'inset 0 1px 0 var(--wm-glass-specular), 0 4px 32px rgba(0,0,0,0.10)' }}>
      <CompanyLogo onNavigate={onNavigate} />
      {multiLocationEnabled && <LocationSwitcher />}

      {/* Center: search pill */}
      <div className="flex-1 flex items-center justify-center px-4">
        <button
          onClick={onOpenPalette}
          className="hidden sm:flex items-center justify-between gap-2 h-8 px-3 rounded-lg text-[var(--wm-text-meta)] font-medium transition-colors flex-shrink-0 border border-gray-300 dark:border-[#364860] bg-gray-50 dark:bg-[#0F1923] text-[#64748B] dark:text-[#94A3B8] hover:border-gray-400 dark:hover:border-[#4A6080] hover:bg-gray-100 dark:hover:bg-[#1B2A3E] w-[220px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E8BF0]"
          aria-label="Quick search (⌘K)"
        >
          <span className="flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <span className="opacity-60">Quick search...</span>
          </span>
          <span className="text-[var(--wm-text-micro)] font-bold px-1.5 py-0.5 rounded flex-shrink-0 bg-black/[0.07] dark:bg-white/10 text-[#64748B] dark:text-[#7D93AE]">
            ⌘K
          </span>
        </button>
      </div>

      <div className="flex items-center gap-1">
        {/* Focus mode toggle */}
        <Tooltip text={focusMode ? 'Exit Focus Mode' : 'Focus Mode'} position="bottom">
        <button
          onClick={() => setFocusMode(!focusMode)}
          className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
            focusMode
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
              : 'text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348] hover:text-[#0F1923] dark:hover:text-[#F8FAFE]'
          }`}
          title={focusMode ? 'Exit Focus Mode' : 'No Distractions'}
        >
          <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
          </svg>
        </button>
        </Tooltip>
        {focusMode && <div className="w-px h-5 bg-gray-200 dark:bg-[#243348] mx-1.5" />}

        {/* Notifications */}
        {!focusMode && (
        <Tooltip text="Notifications" position="bottom">
        <button
          onClick={() => onNavigate('notifications')}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-[#64748B] dark:text-[#7D93AE] hover:bg-gray-100 dark:hover:bg-[#243348] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors relative"
        >
          <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
          </svg>
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[var(--wm-text-caption)] font-bold leading-4 flex items-center justify-center shadow-sm">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
        </Tooltip>
        )}

        {/* Divider */}
        {!focusMode && <div className="w-px h-5 bg-gray-200 dark:bg-[#243348] mx-1.5" />}

        {/* Account dropdown */}
        <AccountDropdown onNavigate={onNavigate} onLogout={onLogout} />
      </div>
    </div>
  );
}

// ─── App ─────────────────────────────────────────────────────────────────────

function AppInner() {
  const { can, currentRole, isPreviewing, exitPreview } = useRoles();
  const { wmCan } = useWmStaff();
  const { xpEnabled } = useFeatureFlags();
  const { focusMode } = useTheme();
  const { addLog } = useAuditLog();
  const { t } = useLanguage();
  const { awardXP, employees } = useGamification();
  const { addNotification } = useNotifications();
  const { estimates, updateEstimate } = useEstimates();
  const { invoices } = useInvoices();

  // ─── Gap bridge: XP auto-award + learning record + notifications on status transitions ──
  // Tracks previous estimate statuses via a stable ref; fires only on genuine changes.
  const prevEstimateStatusesRef = useRef({});
  useEffect(() => {
    const prev = prevEstimateStatusesRef.current;
    estimates.forEach(e => {
      const prevStatus = prev[e.id];
      if (prevStatus && prevStatus !== e.status) {
        const estLabel = `#${e.estimateNumber || e.id}`;
        const custName = e.customerName || 'Customer';
        const total    = e.total ? ` ($${e.total.toLocaleString()})` : '';

        // ── Learning agent — always record terminal outcomes ────────────────
        if (['approved', 'declined', 'converted'].includes(e.status)) {
          recordEstimateOutcome(e, e.status);
        }

        // ── XP + notification on approved ──────────────────────────────────
        if (e.status === 'approved') {
          if (xpEnabled && employees.length > 0) {
            // Attribute to the employee who created the estimate (by name match)
            const creator = employees.find(emp =>
              e.createdBy && emp.name && e.createdBy.toLowerCase().includes(emp.name.split(' ')[0].toLowerCase())
            ) || employees[0];
            awardXP({ employeeId: creator.id, achievementId: 'estimate_approved', xp: 25, note: `Estimate ${estLabel} approved`, awardedBy: 'system' });
          }
          addNotification({ type: 'approval', title: 'Estimate Approved', body: `${custName} approved ${estLabel}${total}`, link: 'estimates', recordId: e.id, icon: 'check' });
        }

        // ── XP + notification on converted ─────────────────────────────────
        if (e.status === 'converted') {
          if (xpEnabled && employees.length > 0) {
            const creator = employees.find(emp =>
              e.createdBy && emp.name && e.createdBy.toLowerCase().includes(emp.name.split(' ')[0].toLowerCase())
            ) || employees[0];
            awardXP({ employeeId: creator.id, achievementId: 'lead_converted', xp: 75, note: `Estimate ${estLabel} converted to job`, awardedBy: 'system' });
          }
          addNotification({ type: 'invoice', title: 'Job Converted', body: `${estLabel} for ${custName} converted to an invoice${total}`, link: 'invoices', recordId: e.id, icon: 'dollar' });
        }

        // ── Notification on declined ────────────────────────────────────────
        if (e.status === 'declined') {
          addNotification({ type: 'estimate', title: 'Estimate Declined', body: `${custName} declined ${estLabel}${total} — consider a follow-up`, link: 'estimates', recordId: e.id, icon: 'document' });
        }
      }
      prev[e.id] = e.status;
    });
  }, [estimates, xpEnabled, employees, awardXP, addNotification]);

  // ─── Gap bridge: auto-expire sent estimates + notify ────────────────────────
  // Runs once on mount to catch any estimates that expired while the app was closed.
  const expiryCheckedRef = useRef(false);
  useEffect(() => {
    if (expiryCheckedRef.current || !estimates.length) return;
    expiryCheckedRef.current = true;
    const now = Date.now();
    estimates.forEach(e => {
      if (e.status === 'sent' && e.expiresAt && new Date(e.expiresAt).getTime() < now) {
        updateEstimate(e.id, { status: 'expired' });
        recordEstimateOutcome(e, 'declined'); // treat expiry as a lost lead
        addNotification({
          type: 'overdue',
          title: 'Estimate Expired',
          body: `${e.customerName || 'Estimate'} #${e.estimateNumber || e.id} expired — follow up now`,
          link: 'estimates',
          recordId: e.id,
          icon: 'alert',
        });
      }
    });
  }, [estimates, updateEstimate, addNotification]);

  // ─── Gap bridge: overdue invoice notifications ───────────────────────────────
  // Runs whenever the invoices array changes; fires once per overdue invoice.
  const notifiedOverdueRef = useRef(new Set());
  useEffect(() => {
    const now = Date.now();
    invoices.forEach(inv => {
      if (notifiedOverdueRef.current.has(inv.id)) return;
      if (['paid', 'void'].includes(inv.status)) return;
      if (!inv.dueAt) return;
      if (new Date(inv.dueAt).getTime() < now && (inv.amountDue || 0) > 0) {
        notifiedOverdueRef.current.add(inv.id);
        const daysOver = Math.floor((now - new Date(inv.dueAt).getTime()) / 86400000);
        addNotification({
          type: 'overdue',
          title: 'Invoice Overdue',
          body: `${inv.invoiceNumber || inv.id} for ${inv.customerName || 'Customer'} is ${daysOver} day${daysOver !== 1 ? 's' : ''} overdue ($${(inv.amountDue || 0).toLocaleString()})`,
          link: 'invoices',
          recordId: inv.id,
          icon: 'clock',
        });
      }
    });
  }, [invoices, addNotification]);

  // ─── Gap bridge: bidirectional invoice ↔ estimate link ──────────────────────
  // When an invoice exists for an estimate, always mark the estimate as converted.
  useEffect(() => {
    invoices.forEach(inv => {
      if (!inv.estimateId) return;
      const est = estimates.find(e => e.id === inv.estimateId);
      if (est && !est.convertedToInvoice) {
        // Invoice is the source of truth — override any prior status
        updateEstimate(inv.estimateId, { convertedToInvoice: true, invoiceId: inv.id, status: 'converted' });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]); // intentional: only watch invoices; estimates + updateEstimate are stable refs

  const { signOut } = useAuth();
  const [setupComplete, setSetupComplete] = useState(() => localStorage.getItem('wm-setup-complete') === 'true');
  const [showWelcome, setShowWelcome] = useState(false); // set to true on successful login if never welcomed

  // Allow HelpPage to replay the welcome screen via a custom window event
  useEffect(() => {
    const handler = () => setShowWelcome(true);
    window.addEventListener('wm-show-welcome', handler);
    return () => window.removeEventListener('wm-show-welcome', handler);
  }, []);

  // App-level navigation
  const [currentView, setCurrentView] = useState('dashboard');
  const [navData, setNavData] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Translated step labels — memoized so they don't rebuild on every render
  const STEPS_T = useMemo(() => [t('step.vehicle'), t('step.customer'), t('step.package'), t('step.material'), t('step.price'), t('step.finalQuote')], [t]);
  const STEP_SUBTITLES_T = useMemo(() => [t('step.vehicle.sub'), t('step.customer.sub'), t('step.package.sub'), t('step.material.sub'), t('step.price.sub'), t('step.finalQuote.sub')], [t]);

  // Estimate state
  const [step, setStep] = useState(0);
  const [selectedCar, setSelectedCar] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);

  // Estimate action state (send / archive / delete confirmations)
  const [sendSent, setSendSent] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Lead prefill data (when navigating to estimate from LeadHub)
  const [leadPrefill, setLeadPrefill] = useState(null);

  const estimateNumber = '0001';

  useEffect(() => {
    if (!localStorage.getItem('wm-welcomed')) setShowWelcome(true);
    addLog('AUTH', 'USER_LOGIN', {
      severity: 'success',
      actor: { role: currentRole, label: ROLES[currentRole]?.label || currentRole },
      target: 'WrapMind App',
      details: { role: currentRole, sessionStart: new Date().toISOString() },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actor = useMemo(() => ({ role: currentRole, label: ROLES[currentRole]?.label || currentRole }), [currentRole]);

  useEffect(() => {
    if (step === 4 && selectedMaterial) {
      const timer = setTimeout(() => {
        setStep(5);
        // Log estimate created (quote complete — all steps filled)
        addLog('ESTIMATE', 'ESTIMATE_CREATED', {
          severity: 'success',
          actor,
          target: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : 'Unknown Vehicle',
          details: {
            estimateNumber,
            vehicle: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : '—',
            customer: selectedCustomer ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || selectedCustomer.name || '—' : '—',
            package: selectedPackage?.name || '—',
            material: selectedMaterial?.name || '—',
          },
        });
        // Analytics: estimate completed
        logEvent(EVENT_TYPES.ESTIMATE_COMPLETED, {
          vehicle: selectedCar ? {
            year:  selectedCar.year,
            make:  selectedCar.make,
            model: selectedCar.model,
            trim:  selectedCar.trim  || undefined,
            vin:   selectedCar.vin   || undefined,
          } : undefined,
          pricing: {
            package_id:    selectedPackage?.id,
            package_name:  selectedPackage?.name,
            material_id:   selectedMaterial?.id,
            material_name: selectedMaterial?.name,
          },
          metadata: { estimate_number: estimateNumber },
        });
        // Auto-award XP when estimate is created (if XP system enabled)
        if (xpEnabled && employees.length > 0) {
          awardXP({ employeeId: employees[0].id, achievementId: 'estimate_created', xp: 10, note: `Estimate #${estimateNumber}`, awardedBy: 'system' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedMaterial]);

  const handleNavigate = useCallback((view, data = null) => {
    if (view === 'estimate') {
      setSelectedCar(null);
      setSelectedCustomer(null);
      setSelectedPackage(null);
      setSelectedMaterial(null);
      setStep(0);
      setSendSent(false);
      setConfirmArchive(false);
      setConfirmDelete(false);
      if (data?.prefill) {
        setLeadPrefill(data.prefill);
      } else {
        setLeadPrefill(null);
      }
      addLog('ESTIMATE', 'ESTIMATE_STARTED', {
        severity: 'info',
        actor,
        target: `Estimate #${estimateNumber}`,
        details: { initiatedFrom: currentView },
      });
    }
    setNavData(data);
    setCurrentView(view);
  }, [addLog, actor, currentView]);

  const handleSelectCar = (car) => {
    setSelectedCar(car);
    setSelectedCustomer(null);
    setSelectedPackage(null);
    setSelectedMaterial(null);
    setStep(1);
    addLog('ESTIMATE', 'ESTIMATE_OPENED', {
      severity: 'info',
      actor,
      target: `${car.year} ${car.make} ${car.model}`,
      details: { estimateNumber, vin: car.vin || '—', vehicleType: car.vehicle_type || '—' },
    });
    logEvent(EVENT_TYPES.VEHICLE_SELECTED, {
      vehicle: {
        year:  car.year,
        make:  car.make,
        model: car.model,
        trim:  car.trim  || undefined,
        vin:   car.vin   || undefined,
        body_style: car.vehicle_type || undefined,
      },
    });
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setStep(2); // → Package step
    logEvent(EVENT_TYPES.SESSION_STEP_CHANGED, { metadata: { step: 2, label: 'Customer' } });
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    setStep(3);
    logEvent(EVENT_TYPES.PACKAGE_SELECTED, {
      pricing: { package_id: pkg.id, package_name: pkg.name },
    });
  };

  const handleSelectMaterial = (material) => {
    setSelectedMaterial(material);
    setStep(4);
    logEvent(EVENT_TYPES.MATERIAL_SELECTED, {
      pricing: { material_id: material.id, material_name: material.name },
    });
  };

  // ── Analytics: session start ─────────────────────────────────────────────
  useEffect(() => {
    logEvent(EVENT_TYPES.SESSION_STARTED, { metadata: { role: currentRole } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Keyboard shortcut: Cmd+K / Ctrl+K → Command Palette ──────────────────
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // ── First-time setup wizard (admin / superadmin only) ──────────────────────
  const isAdminUser = currentRole === 'superadmin' || currentRole === 'admin';
  if (!setupComplete && isAdminUser) {
    return <SetupWizard onComplete={() => setSetupComplete(true)} />;
  }

  // ── Authenticated layout ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8FAFE] dark:bg-[#0F1923] flex" style={{ isolation: 'isolate' }}>

      {/* ── Liquid glass orb backdrop ──────────────────────────────────────────
          Fixed blobs of color that float behind everything. Elements with
          backdrop-filter blur these, producing genuine frosted-glass.        */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        {/* Blue orb — top-left anchor */}
        <div style={{
          position: 'absolute', borderRadius: '50%',
          width: 700, height: 700, top: -220, left: -160,
          background: `radial-gradient(circle, var(--wm-orb-1) 0%, transparent 68%)`,
          filter: 'blur(72px)',
          animation: 'wm-orb-drift-1 20s ease-in-out infinite',
          willChange: 'transform',
        }} />
        {/* Indigo orb — bottom-right anchor */}
        <div style={{
          position: 'absolute', borderRadius: '50%',
          width: 620, height: 620, bottom: -180, right: -120,
          background: `radial-gradient(circle, var(--wm-orb-2) 0%, transparent 68%)`,
          filter: 'blur(88px)',
          animation: 'wm-orb-drift-2 26s ease-in-out infinite',
          willChange: 'transform',
        }} />
        {/* Cyan orb — mid-canvas float */}
        <div style={{
          position: 'absolute', borderRadius: '50%',
          width: 480, height: 480, top: '35%', left: '45%',
          background: `radial-gradient(circle, var(--wm-orb-3) 0%, transparent 68%)`,
          filter: 'blur(96px)',
          animation: 'wm-orb-drift-3 30s ease-in-out infinite',
          willChange: 'transform',
        }} />
      </div>
      <style>{`
        @media print {
          @page { margin: 0; }
          header, .print-button, aside, .mobile-bottom-nav, .top-bar { display: none !important; }
          body { background: white !important; color: black !important; }
        }
      `}</style>

      {/* One-time welcome screen */}
      {showWelcome && (
        <WelcomeScreen
          onNavigate={handleNavigate}
          onDismiss={() => setShowWelcome(false)}
        />
      )}

      {/* Global celebration overlay — listens for wm-celebrate events */}
      <CelebrationOverlay />

      {/* Global toast notifications */}
      <ToastStack />

      {/* Global command palette — Cmd+K */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onNavigate={handleNavigate}
      />

      {/* App navigation sidebar — hidden in focus mode */}
      {!focusMode && <SideNav currentView={currentView} onNavigate={handleNavigate} />}

      {/* Main content column */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Global top control bar */}
        <TopBar
          onNavigate={handleNavigate}
          onLogout={() => { resetSession(); signOut(); }}
          onOpenPalette={() => setPaletteOpen(true)}
        />
        {!focusMode && <Ticker />}

        <div key={currentView} className="wm-page-enter flex-1 flex flex-col min-w-0 overflow-hidden pt-3">
        {currentView === 'dashboard' ? (
          <Dashboard />
        ) : currentView === 'settings' ? (
          <SettingsShell initialTab={navData?.settingsTab || 'general'} />
        ) : currentView === 'performance' && xpEnabled ? (
          <PerformancePage can={can} />
        ) : currentView === 'audit-log' && wmCan('internal.audit') ? (
          <AuditLog />
        ) : currentView === 'site-bugs' && wmCan('internal.bugs') ? (
          <SiteBugsPage />
        ) : currentView === 'workflow' ? (
          <WorkflowPage onNavigate={handleNavigate} />
        ) : currentView === 'leadhub' ? (
          <LeadHubPage onNavigate={handleNavigate} />
        ) : currentView === 'lists-customers' ? (
          <CustomersPage onNavigate={handleNavigate} />
        ) : currentView === 'lists-vehicles' ? (
          <VehiclesPage onNavigate={handleNavigate} />
        ) : currentView === 'lists-manufacturers' ? (
          <ManufacturersPage onNavigate={handleNavigate} />
        ) : currentView === 'estimates' ? (
          <EstimatesPage onNavigate={handleNavigate} initialEstimateId={navData?.initialId} />
        ) : currentView === 'invoices' ? (
          <InvoicesPage onNavigate={handleNavigate} initialInvoiceId={navData?.initialId} />
        ) : currentView === 'notifications' ? (
          <NotificationsPage onNavigate={handleNavigate} />
        ) : currentView === 'reports' ? (
          <ReportsPage onNavigate={handleNavigate} />
        ) : currentView === 'client-portal' ? (
          <ClientPortalPage onNavigate={handleNavigate} />
        ) : currentView === 'help' ? (
          <HelpPage />
        ) : currentView === 'intelligence' && wmCan('internal.intelligence') ? (
          <IntelligencePage />
        ) : currentView === 'shops' && wmCan('internal.shop_profiles') ? (
          <ShopProfilesPage />
        ) : currentView === 'staff-portal' && wmCan('internal.shop_profiles') ? (
          <StaffPortal onNavigate={handleNavigate} />
        ) : currentView === 'my-accounts' && wmCan('org.enter') ? (
          <MyAccounts onNavigate={handleNavigate} />
        ) : currentView === 'staff-settings' ? (
          <StaffSettingsTab />
        ) : currentView === 'marketing' ? (
          <MarketingPage onNavigate={handleNavigate} />
        ) : currentView === 'scheduling' ? (
          <SchedulingPage />
        ) : currentView === 'subcontractors' ? (
          <SubcontractorsPage onNavigate={handleNavigate} />
        ) : currentView === 'orders' ? (
          <WorkOrdersPage />
        ) : currentView === 'inventory' ? (
          <InventoryPage />
        ) : currentView === 'parcel-tracking' ? (
          <ParcelTrackingView />
        ) : currentView !== 'estimate' ? (
          <PlaceholderView view={currentView} />
        ) : (
          /* ── Estimate view ── */
          <>
            {/* Estimate header */}
            <header className="bg-white dark:bg-[#1B2A3E] border-b border-gray-200 dark:border-[#243348] flex-shrink-0">
              {/* Row 1: title + actions */}
              <div className="h-10 flex items-center px-4 gap-3">
                <button
                  onClick={() => setCurrentView('workflow')}
                  className="text-[#64748B] dark:text-[#7D93AE] hover:text-[#0F1923] dark:hover:text-[#F8FAFE] transition-colors mr-1"
                  title="Back"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="text-[var(--wm-text-body)] font-medium text-[#0F1923] dark:text-[#F8FAFE] whitespace-nowrap">
                    Estimate #{estimateNumber}
                  </span>
                  {selectedCar && (
                    <span className="hidden sm:inline text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE] font-mono truncate">
                      · {selectedCar.year} {selectedCar.make} {selectedCar.model}
                    </span>
                  )}
                </div>
                {step === 5 && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="print-button"
                      onClick={() => {
                        addLog('ESTIMATE', 'ESTIMATE_PRINTED', {
                          severity: 'info',
                          actor,
                          target: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : `Estimate #${estimateNumber}`,
                          details: { estimateNumber, format: 'PDF/Print' },
                        });
                        logEvent(EVENT_TYPES.ESTIMATE_PRINTED, {
                          vehicle: selectedCar ? { year: selectedCar.year, make: selectedCar.make, model: selectedCar.model } : undefined,
                          metadata: { estimate_number: estimateNumber },
                        });
                        window.print();
                      }}
                    >
                      Print
                    </Button>
                  </div>
                )}
              </div>

              {/* Lead prefill banner */}
              {leadPrefill && (
                <div className="flex items-center gap-2 px-4 py-1.5 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-100 dark:border-blue-900/40 text-[var(--wm-text-ui)] text-blue-700 dark:text-blue-400">
                  <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Pre-filled from lead: <strong>{leadPrefill.customerName}</strong> · {leadPrefill.vehicleLabel || 'No vehicle'} · Budget: {leadPrefill.budget ? `$${leadPrefill.budget.toLocaleString()}` : '—'}</span>
                  <button onClick={() => setLeadPrefill(null)} className="ml-auto text-blue-400 hover:text-blue-600">✕</button>
                </div>
              )}

              {/* Row 2: step tabs — mobile: numbered circles, desktop: text tabs */}
              {/* Mobile step indicator */}
              <div className="sm:hidden flex items-center border-t border-gray-200 dark:border-[#243348] px-4 py-2.5">
                {STEPS_T.map((label, i) => {
                  const isCompleted = i < step;
                  const isCurrent = i === step;
                  const isDisabled = i > step;
                  return (
                    <div key={label} className="flex items-center flex-1 last:flex-none">
                      <button
                        onClick={() => !isDisabled && setStep(i)}
                        disabled={isDisabled}
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-[var(--wm-text-meta)] font-semibold transition-all flex-shrink-0
                          ${isCurrent
                            ? 'bg-[#2E8BF0] text-white ring-2 ring-[#2E8BF0]/30'
                            : isCompleted
                              ? 'bg-[#2E8BF0]/15 text-[#2E8BF0] dark:bg-[#2E8BF0]/25'
                              : 'bg-gray-100 dark:bg-[#1B2A3E] text-gray-300 dark:text-[#4A6380] cursor-not-allowed'
                          }`}
                        aria-label={label}
                      >
                        {isCompleted ? (
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          i + 1
                        )}
                      </button>
                      {i < STEPS_T.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 rounded-full transition-colors ${
                          i < step ? 'bg-[#2E8BF0]/40' : 'bg-gray-200 dark:bg-[#243348]'
                        }`} />
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Desktop step tabs */}
              <div className="hidden sm:flex border-t border-gray-200 dark:border-[#243348] overflow-x-auto">
                {STEPS_T.map((label, i) => {
                  const isCompleted = i < step;
                  const isCurrent = i === step;
                  const isDisabled = i > step;
                  return (
                    <button
                      key={label}
                      onClick={() => !isDisabled && setStep(i)}
                      disabled={isDisabled}
                      className={`flex items-center gap-1.5 px-4 py-2.5 text-[var(--wm-text-ui)] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors flex-shrink-0
                        ${isCurrent
                          ? 'text-[#2E8BF0] border-[#2E8BF0]'
                          : isCompleted
                            ? 'text-[#64748B] dark:text-[#7D93AE] border-transparent hover:text-[#0F1923] dark:hover:text-[#F8FAFE] hover:border-gray-200 dark:hover:border-[#243348]'
                            : 'text-gray-300 dark:text-[#4A6380] border-transparent cursor-not-allowed'
                        }`}
                    >
                      {isCompleted && !isCurrent && (
                        <svg className="w-3 h-3 text-[#2E8BF0] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {label}
                    </button>
                  );
                })}
              </div>
            </header>

            {/* Estimate body */}
            <div className="flex flex-1 overflow-hidden">
              <main className="flex-1 overflow-auto p-4 lg:p-6 pb-20 md:pb-6 bg-[#F8FAFE] dark:bg-[#0F1923]">
                {/* Step subtitle */}
                <p className={`text-[var(--wm-text-ui)] mb-4 ${step === 0 ? 'text-[#00D4FF]' : 'text-[#64748B] dark:text-[#7D93AE]'}`}>{STEP_SUBTITLES_T[step]}</p>

                {/* Vehicle context strip — steps 1–5 */}
                {step >= 1 && selectedCar && (
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-4 px-3 py-2 bg-white dark:bg-[#1B2A3E] border border-gray-200 dark:border-[#243348] rounded text-[var(--wm-text-body)]">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded bg-[#2E8BF0]/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-[#2E8BF0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />
                        </svg>
                      </div>
                      <span className="font-medium text-[#0F1923] dark:text-[#F8FAFE] text-[var(--wm-text-body)]">{selectedCar.year} {selectedCar.make} {selectedCar.model}</span>
                      {selectedCar.trim && <span className="text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">{selectedCar.trim}</span>}
                      <span className="text-[var(--wm-text-ui)] px-1.5 py-0.5 rounded border border-gray-200 dark:border-[#243348] text-[#64748B] dark:text-[#7D93AE] bg-[#F8FAFE] dark:bg-[#0F1923]">{selectedCar.vehicle_type}</span>
                    </div>
                    {selectedPackage && (
                      <div className="flex items-center gap-1.5 text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
                        <span className="text-gray-300 dark:text-[#243348]">·</span>
                        <span>{selectedPackage.name}</span>
                      </div>
                    )}
                    {selectedMaterial && (
                      <div className="flex items-center gap-1.5 text-[var(--wm-text-ui)] text-[#64748B] dark:text-[#7D93AE]">
                        <span className="text-gray-300 dark:text-[#243348]">·</span>
                        <span>{selectedMaterial.name}</span>
                      </div>
                    )}
                  </div>
                )}

                {step === 0 && <VinSearch onSelect={handleSelectCar} selectedCar={selectedCar} />}

                {step === 1 && (
                  <CustomerForm
                    initialData={selectedCustomer || {}}
                    onContinue={handleSelectCustomer}
                    onSkip={() => { setSelectedCustomer(null); setStep(2); }}
                  />
                )}

                {step === 2 && (
                  <>
                    <p className="text-[var(--wm-text-ui)] font-medium uppercase tracking-wide text-[#64748B] dark:text-[#7D93AE] mb-3">Available Packages</p>
                    <PackageCard selectedPackage={selectedPackage} onSelect={handleSelectPackage} carId={selectedCar?.id} />
                  </>
                )}

                {step === 3 && (
                  <>
                    <p className="text-[var(--wm-text-ui)] font-medium uppercase tracking-wide text-[#64748B] dark:text-[#7D93AE] mb-3">Wrap Material</p>
                    <MaterialCard selectedMaterial={selectedMaterial} onSelect={handleSelectMaterial} carId={selectedCar?.id} />
                  </>
                )}

                {step === 4 && (
                  <PriceSummary car={selectedCar} selectedPackage={selectedPackage} selectedMaterial={selectedMaterial} />
                )}

                {step === 5 && (
                  <>
                    <PriceSummary car={selectedCar} selectedPackage={selectedPackage} selectedMaterial={selectedMaterial} expanded />

                    {/* ── Primary actions ── */}
                    <div className="mt-4 flex gap-2">
                      {/* Print */}
                      <Button
                        variant="primary"
                        className="print-button flex-1"
                        onClick={() => {
                          addLog('ESTIMATE', 'ESTIMATE_PRINTED', {
                            severity: 'info',
                            actor,
                            target: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : `Estimate #${estimateNumber}`,
                            details: { estimateNumber, format: 'PDF/Print' },
                          });
                          logEvent(EVENT_TYPES.ESTIMATE_PRINTED, {
                            vehicle: selectedCar ? { year: selectedCar.year, make: selectedCar.make, model: selectedCar.model } : undefined,
                            metadata: { estimate_number: estimateNumber },
                          });
                          window.print();
                        }}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                        </svg>
                        Print / Save PDF
                      </Button>

                      {/* Send */}
                      {sendSent ? (
                        <div className="flex-1 h-8 rounded bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/40 flex items-center justify-center gap-1.5 text-[var(--wm-text-ui)] font-medium text-emerald-700 dark:text-emerald-400">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                          Estimate Sent
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            setSendSent(true);
                            addLog('ESTIMATE', 'ESTIMATE_SENT', {
                              severity: 'success',
                              actor,
                              target: selectedCustomer
                                ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim() || selectedCustomer.name || 'Customer'
                                : 'Customer',
                              details: {
                                estimateNumber,
                                vehicle: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : '—',
                                customer: selectedCustomer?.email || selectedCustomer?.name || '—',
                                package: selectedPackage?.name || '—',
                              },
                            });
                          }}
                          className="flex-1 h-8 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] text-[var(--wm-text-ui)] font-medium text-[#0F1923] dark:text-[#F8FAFE] hover:bg-gray-50 dark:hover:bg-[#243348] transition-colors flex items-center justify-center gap-1.5"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                          </svg>
                          Send Estimate
                        </button>
                      )}
                    </div>

                    {/* ── Secondary actions ── */}
                    <div className="mt-2 flex gap-2">
                      {/* Archive */}
                      {confirmArchive ? (
                        <div className="flex-1 flex items-center gap-1.5 px-3 h-8 rounded bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40">
                          <span className="text-[var(--wm-text-ui)] text-amber-700 dark:text-amber-400 flex-1">Archive this estimate?</span>
                          <button
                            onClick={() => {
                              addLog('ESTIMATE', 'ESTIMATE_ARCHIVED', {
                                severity: 'warning',
                                actor,
                                target: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : `Estimate #${estimateNumber}`,
                                details: { estimateNumber, vehicle: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : '—', customer: selectedCustomer?.name || '—' },
                              });
                              handleNavigate('workflow');
                            }}
                            className="text-[var(--wm-text-meta)] font-semibold text-amber-700 dark:text-amber-400 hover:underline"
                          >Yes</button>
                          <span className="text-amber-300 dark:text-amber-700">·</span>
                          <button onClick={() => setConfirmArchive(false)} className="text-[var(--wm-text-meta)] text-amber-600 dark:text-amber-500 hover:underline">No</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmArchive(true)}
                          className="flex-1 h-8 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] hover:bg-amber-50 dark:hover:bg-amber-900/15 hover:text-amber-700 dark:hover:text-amber-400 hover:border-amber-200 dark:hover:border-amber-800/40 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3l3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                          </svg>
                          Archive
                        </button>
                      )}

                      {/* Delete */}
                      {confirmDelete ? (
                        <div className="flex-1 flex items-center gap-1.5 px-3 h-8 rounded bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/40">
                          <span className="text-[var(--wm-text-ui)] text-red-700 dark:text-red-400 flex-1">Permanently delete?</span>
                          <button
                            onClick={() => {
                              addLog('ESTIMATE', 'ESTIMATE_DELETED', {
                                severity: 'critical',
                                actor,
                                target: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : `Estimate #${estimateNumber}`,
                                details: { estimateNumber, vehicle: selectedCar ? `${selectedCar.year} ${selectedCar.make} ${selectedCar.model}` : '—', customer: selectedCustomer?.name || '—', package: selectedPackage?.name || '—' },
                              });
                              handleNavigate('workflow');
                            }}
                            className="text-[var(--wm-text-meta)] font-semibold text-red-700 dark:text-red-400 hover:underline"
                          >Delete</button>
                          <span className="text-red-300 dark:text-red-800">·</span>
                          <button onClick={() => setConfirmDelete(false)} className="text-[var(--wm-text-meta)] text-red-600 dark:text-red-500 hover:underline">Cancel</button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(true)}
                          className="flex-1 h-8 rounded border border-gray-200 dark:border-[#243348] bg-white dark:bg-[#1B2A3E] text-[var(--wm-text-ui)] font-medium text-[#64748B] dark:text-[#7D93AE] hover:bg-red-50 dark:hover:bg-red-900/15 hover:text-red-600 dark:hover:text-red-400 hover:border-red-200 dark:hover:border-red-800/40 transition-colors flex items-center justify-center gap-1.5"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                          Delete
                        </button>
                      )}
                    </div>
                  </>
                )}
              </main>

              <EstimateRightPanel
                selectedCar={selectedCar}
                selectedCustomer={selectedCustomer}
                selectedPackage={selectedPackage}
                selectedMaterial={selectedMaterial}
                currentStep={step}
              />
            </div>
          </>
        )}
        </div>
      </div>

      <IOSAddToHomeScreen />
      {!focusMode && <WrapMindChat />}

      {/* Role preview escape hatch — always visible, never permission-gated */}
      {isPreviewing && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9997,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'rgba(15,25,35,0.92)',
            border: '1px solid rgba(245,158,11,0.45)',
            borderRadius: 999,
            padding: '7px 14px 7px 12px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.45), 0 0 0 1px rgba(245,158,11,0.15)',
            backdropFilter: 'blur(12px)',
            userSelect: 'none',
            pointerEvents: 'auto',
          }}
        >
          {/* Amber dot */}
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: '#F59E0B',
            boxShadow: '0 0 6px 2px rgba(245,158,11,0.5)',
            flexShrink: 0,
          }} />
          <span style={{ fontSize: 'var(--wm-text-ui)', color: '#F8FAFE', fontWeight: 500, whiteSpace: 'nowrap' }}>
            Previewing as <strong style={{ color: '#FCD34D' }}>{ROLES[currentRole]?.label ?? currentRole}</strong>
          </span>
          <button
            onClick={exitPreview}
            style={{
              marginLeft: 4,
              fontSize: 'var(--wm-text-meta)',
              fontWeight: 600,
              color: '#F59E0B',
              background: 'rgba(245,158,11,0.12)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 999,
              padding: '3px 10px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(245,158,11,0.22)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(245,158,11,0.12)'}
          >
            Exit preview
          </button>
        </div>
      )}

      {/* Mobile bottom tab bar — hidden on md+ screens */}
      <BottomTabBar currentView={currentView} onNavigate={handleNavigate} />

      {/* Staff impersonation banner — stacks above role-preview banner when both visible */}
      <StaffImpersonationBanner bottomOffset={isPreviewing ? 56 : 0} />
    </div>
  );
}

// ─── Grouped providers ────────────────────────────────────────────────────────
// Split into four logical tiers so the App() function stays readable and each
// group's responsibilities are clear. Nesting order within each group matters
// (outer providers are available to inner ones).

/** Infrastructure: logging, theming, i18n, ticker — no data dependencies */
function InfraProviders({ children }) {
  return (
    <AuditLogProvider>
      <ThemeProvider>
        <LanguageProvider>
          <TickerProvider>
            {children}
          </TickerProvider>
        </LanguageProvider>
      </ThemeProvider>
    </AuditLogProvider>
  );
}

/** Config: feature flags, units, roles — depends on infra */
function ConfigProviders({ children }) {
  return (
    <FeatureFlagsProvider>
      <UnitsProvider>
        <RolesProvider>
          {children}
        </RolesProvider>
      </UnitsProvider>
    </FeatureFlagsProvider>
  );
}

/** Feature: gamification, feedback, notifications — depends on config */
function FeatureProviders({ children }) {
  return (
    <ToastProvider>
      <GamificationProvider>
        <FeedbackProvider>
          <NotificationsProvider>
            {children}
          </NotificationsProvider>
        </FeedbackProvider>
      </GamificationProvider>
    </ToastProvider>
  );
}

/** Data: all domain data contexts — CustomerProvider must be innermost
 *  because it reads from the other four via useContext hooks */
function DataProviders({ children }) {
  return (
    <LocationProvider>
      <InventoryProvider>
        <EstimateProvider>
          <InvoiceProvider>
            <SchedulingProvider>
              <LeadsProvider>
              <MarketingProvider>
                <WorkOrderProvider>
                  <SubcontractorProvider>
                    <CustomerProvider>
                      {children}
                    </CustomerProvider>
                  </SubcontractorProvider>
                </WorkOrderProvider>
              </MarketingProvider>
              </LeadsProvider>
            </SchedulingProvider>
          </InvoiceProvider>
        </EstimateProvider>
      </InventoryProvider>
    </LocationProvider>
  );
}

function AuthGate() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <AuthPage />;

  return (
    <WmStaffProvider>
      <ConfigProviders>
        <FeatureProviders>
          <DataProviders>
            <AppInner />
          </DataProviders>
        </FeatureProviders>
      </ConfigProviders>
    </WmStaffProvider>
  );
}

// ─── PublicEstimateRoute ──────────────────────────────────────────────────────
// Matches /e/:token — renders CustomerEstimateView without auth requirement.
// We only need EstimateProvider (which loads from localStorage/seed in dev mode).
function PublicEstimateRoute({ token }) {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LocationProvider>
          <EstimateProvider>
            <CustomerEstimateView token={token} />
          </EstimateProvider>
        </LocationProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function App() {
  // Intercept public estimate share URLs before auth gate
  const pathname = window.location.pathname;
  const shareMatch = pathname.match(/^\/e\/([A-Za-z0-9]{6,48})$/);
  if (shareMatch) {
    return <PublicEstimateRoute token={shareMatch[1]} />;
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <InfraProviders>
          <AuthGate />
        </InfraProviders>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
